import React, { useEffect, useRef, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";

const CHECK_EVERY_MS = 60 * 1000;
const MAIN_JS = /\/static\/js\/(main\.[A-Za-z0-9]+\.js)/;

const runningBundle = () => {
  const src = Array.from(document.scripts)
    .map((s) => s.src)
    .find((u) => MAIN_JS.test(u));
  return src ? src.match(MAIN_JS)[1] : null;
};

/**
 * Watches the deployed index.html. When it points at a different main bundle
 * than the one this tab is running, a new version has been published: show a
 * toast with a Refresh button.
 */
export default function NewVersionToast() {
  const [available, setAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const current = useRef(runningBundle());

  useEffect(() => {
    if (!current.current) return undefined; // dev server: no hashed bundle to compare

    let stopped = false;
    const check = async () => {
      try {
        const res = await fetch(`/index.html?_=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const latest = (await res.text()).match(MAIN_JS);
        if (!stopped && latest && latest[1] !== current.current) {
          setAvailable(true);
        }
      } catch (e) { /* offline or blocked: try again next time */ }
    };

    const timer = setInterval(check, CHECK_EVERY_MS);
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
    };
  }, []);

  return (
    <Snackbar
      open={available && !dismissed}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      sx={{ zIndex: 20000 }}
    >
      <Alert
        severity="info"
        variant="filled"
        onClose={() => setDismissed(true)}
        action={
          <>
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Refresh
            </Button>
            <Button color="inherit" size="small" onClick={() => setDismissed(true)}>
              Later
            </Button>
          </>
        }
      >
        A new version is available.
      </Alert>
    </Snackbar>
  );
}
